// src/services/allauthApi.js
// Dedicated API client for django-allauth authentication
// Pattern adapted from weaversworld_frontend

import axios from 'axios';
import { logger, getCsrfToken, fetchCsrfToken } from './utils';

// Client type - use 'browser' for cookie-based auth (more secure than 'app' token-based)
const CLIENT_TYPE = 'browser';

const allauthApiClient = axios.create({
  baseURL: `/_allauth/${CLIENT_TYPE}/v1`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add interceptor for CSRF token handling
allauthApiClient.interceptors.request.use(async config => {
  // For methods that modify data, ensure we have a fresh CSRF token
  if (['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
    // For auth endpoints, fetch a fresh token first
    if (config.url.startsWith('/auth/')) {
      await fetchCsrfToken();
    }

    // Get the token from cookies and add it to the request headers
    const token = getCsrfToken();
    if (token) {
      config.headers['X-CSRFToken'] = token;
    }
  }

  return config;
});

/**
 * Get current session status and authentication info
 */
export const getSession = async () => {
  try {
    const response = await allauthApiClient.get('/auth/session');
    return response.data;
  } catch (error) {
    // 401 means not authenticated - expected response
    if (error.response?.status === 401) {
      return error.response.data;
    }

    // 410 Gone means session expired
    if (error.response?.status === 410) {
      logger.debug('AllauthAPI', 'Session expired (410 Gone)');
      return {
        status: 401,
        meta: { is_authenticated: false }
      };
    }

    logger.error('AllauthAPI', 'Error getting session', error);
    throw error;
  }
};

/**
 * Login with email and password
 */
export const login = async (email, password) => {
  try {
    const response = await allauthApiClient.post('/auth/login', {
      email,
      password
    });
    return response.data;
  } catch (error) {
    // 401 might mean additional auth steps needed (2FA)
    if (error.response?.status === 401) {
      return error.response.data;
    }

    logger.error('AllauthAPI', 'Login failed', error);
    throw error;
  }
};

/**
 * Logout the current user
 */
export const logout = async () => {
  try {
    await allauthApiClient.delete('/auth/session');
    return { success: true };
  } catch (error) {
    // 401 after logout is expected (means we're logged out)
    if (error.response?.status === 401) {
      logger.debug('AllauthAPI', 'Logged out successfully');
      return { success: true };
    }

    logger.error('AllauthAPI', 'Logout failed', error);
    throw error;
  }
};

export default allauthApiClient;
